import { keys } from 'services/utils'
import {
  ServiceDiscriminator,
  TimeBeforeHint,
  WithId,
  WithIdAndName,
} from 'types/common'
import { QuestionType } from 'types/form'
import {
  parseTimeBeforeHint,
  writeTimeBeforeHint,
} from 'ui-shared/Hints/common'
import { WithTimeBeforeHint } from 'ui-shared/Hints/types'
import { APICfg } from '../api'
import { parseKDocks } from './catalog/utils'
import { uploadOrKeepMultipleFiles } from './common'
import { APIDocument, FormMultipleDocuments } from './entities'

type WithServicesConfigId = { id: 'servicesconfig' }

export type BriefQuestionBase = WithId & {
  text: string
  type: QuestionType
  mandatory: boolean
  options?: string[]
  // #1180
  notActiveWhenServices: string[]
}
export type BriefQuestion = BriefQuestionBase & {
  products: number[]
}

export type GeneralBriefQuestion = BriefQuestionBase & {
  activeWhenServices: string[]
}

type APIServiceConfig = {
  eventKampaayerDocs: APIDocument[]
  brief: BriefQuestion[]
  serviceDiscriminator: ServiceDiscriminator
  advanceReservationMin: number
}

type APIFoodService = APIServiceConfig & {
  timeBeforeHint: TimeBeforeHint[]
}

type APIGeneralServiceConfig = {
  brief: GeneralBriefQuestion[]
  serviceDiscriminator: ServiceDiscriminator
}

export type APIProfessionalServiceConfig = {
  section: WithIdAndName
  brief: BriefQuestion[]
}

export type APIServicesConfig = {
  generalService: APIGeneralServiceConfig
  foodService: APIFoodService
  rentService: APIServiceConfig
  musicService: APIServiceConfig
  drinkService: APIServiceConfig
  virtualService: APIServiceConfig
  physicalService: APIServiceConfig
  locationService: APIServiceConfig
  professionalService: {
    advanceReservationMin: number
    services: APIProfessionalServiceConfig[]
    serviceDiscriminator: ServiceDiscriminator
  }
}

export type ServiceConfig = Omit<APIServiceConfig, 'eventKampaayerDocs'> & {
  eventKampaayerDocs: FormMultipleDocuments
}

type FoodService = ServiceConfig & WithTimeBeforeHint

type GeneralServiceConfig = APIGeneralServiceConfig

export type ProfessionalServiceConfig = APIProfessionalServiceConfig

export type ServicesConfig = WithServicesConfigId & {
  generalService: GeneralServiceConfig
  foodService: FoodService
  rentService: ServiceConfig
  musicService: ServiceConfig
  drinkService: ServiceConfig
  virtualService: ServiceConfig
  physicalService: ServiceConfig
  locationService: ServiceConfig
  professionalService: {
    advanceReservationMin: number
    services: ProfessionalServiceConfig[]
    serviceDiscriminator: ServiceDiscriminator
  }
}

type ServiceConfigToAPI = {
  advanceReservationMin: number
  eventKampaayerDocs: string[]
}

type FoodServiceToAPI = ServiceConfigToAPI & {
  timeBeforeHint: TimeBeforeHint[]
}

type ProfessionalServiceConfigToAPI = {
  section: number
  brief: BriefQuestion[]
}

type ServicesConfigToAPI = {
  generalService: GeneralServiceConfig
  foodService: FoodServiceToAPI
  rentService: ServiceConfigToAPI
  musicService: ServiceConfigToAPI
  drinkService: ServiceConfigToAPI
  virtualService: ServiceConfigToAPI
  physicalService: ServiceConfigToAPI
  locationService: ServiceConfigToAPI
  professionalService: {
    advanceReservationMin: number
    services: ProfessionalServiceConfigToAPI[]
    serviceDiscriminator: ServiceDiscriminator
  }
}

const apiCfg: APICfg = {
  endpoint: 'serviceconfig',
  isSingleEntity: true,
  UPDATE_METHOD: 'POST',
  parse: ({
    foodService,
    rentService,
    musicService,
    drinkService,
    virtualService,
    physicalService,
    locationService,
    ...res
  }: APIServicesConfig): ServicesConfig => {
    return {
      ...res,
      foodService: {
        ...foodService,
        ...parseTimeBeforeHint(foodService),
        eventKampaayerDocs: parseKDocks(foodService.eventKampaayerDocs),
      },
      rentService: {
        ...rentService,
        eventKampaayerDocs: parseKDocks(rentService.eventKampaayerDocs),
      },
      musicService: {
        ...musicService,
        eventKampaayerDocs: parseKDocks(musicService.eventKampaayerDocs),
      },
      drinkService: {
        ...drinkService,
        eventKampaayerDocs: parseKDocks(drinkService.eventKampaayerDocs),
      },
      virtualService: {
        ...virtualService,
        eventKampaayerDocs: parseKDocks(virtualService.eventKampaayerDocs),
      },
      physicalService: {
        ...physicalService,
        eventKampaayerDocs: parseKDocks(physicalService.eventKampaayerDocs),
      },
      locationService: {
        ...locationService,
        eventKampaayerDocs: parseKDocks(locationService.eventKampaayerDocs),
      },
      professionalService: res.professionalService.services.length
        ? res.professionalService
        : {
            advanceReservationMin:
              res.professionalService.advanceReservationMin ?? 0,
            services: res.professionalService.services,
            serviceDiscriminator: res.professionalService.serviceDiscriminator,
          },
      id: 'servicesconfig',
    }
  },
  write: async (req: ServicesConfig): Promise<ServicesConfigToAPI> => {
    const { id, professionalService, generalService, ...rest } = req
    const mappedProfessionalServices = professionalService.services.map(
      (s) => ({
        ...s,
        section: s.section.id,
      })
    )
    const servicesDocuments = await Promise.all(
      keys(rest).map((servName) =>
        uploadOrKeepMultipleFiles(req[servName].eventKampaayerDocs).then(
          (res) => ({
            servName,
            res,
          })
        )
      )
    )

    // TODO extract our internal models from food hint to not send them to the api
    const mapped = servicesDocuments.reduce(
      (acc, { servName, res }) => ({
        ...acc,
        [servName]: {
          ...req[servName],
          ...(servName === 'foodService'
            ? writeTimeBeforeHint(req.foodService)
            : {}),
          eventKampaayerDocs: res,
        },
      }),
      Object.create(null)
    )

    return {
      ...mapped,
      generalService,
      professionalService: {
        ...req.professionalService,
        services: mappedProfessionalServices,
      },
    }
  },
}

export default apiCfg
